import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { evaluatePassword } from "../lib/passwordStrength";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import Logo from "../components/Logo";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const noToken = !token;
  const { allPassed } = evaluatePassword(pwd);
  const matches = pwd.length > 0 && pwd === confirm;
  const canSubmit = allPassed && matches && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
        data-testid="reset-password-page"
      >
        <div className="flex justify-center mb-7"><Logo size="lg" /></div>

        <div className="premium-card p-8 sm:p-10">
          {noToken ? (
            <div className="text-center" data-testid="reset-missing-token">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-5">
                <AlertTriangle size={26} className="text-red-600" strokeWidth={1.6} />
              </div>
              <h1 className="font-serif-display text-2xl text-brand-text">Link not valid</h1>
              <p className="text-brand-muted mt-3">
                This reset link is missing or malformed. Request a new one to continue.
              </p>
              <Link to="/forgot-password" className="btn-primary mt-6 inline-flex" data-testid="reset-request-new-link">
                Request new link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-2" data-testid="reset-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-5">
                <ShieldCheck size={28} className="text-green-700" strokeWidth={1.6} />
              </div>
              <h1 className="font-serif-display text-3xl text-brand-text">Password updated</h1>
              <p className="text-brand-muted mt-3">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="font-serif-display text-3xl text-brand-text">Set a new password</h1>
              <p className="text-brand-muted mt-1.5">Choose something strong — your account, your control.</p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="eyebrow block mb-2">New password</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-2xl border-2 border-brand focus-within:border-brand-primary bg-brand-bg">
                    <Lock size={16} className="text-brand-muted" strokeWidth={1.6} />
                    <input
                      type="password"
                      required
                      autoFocus
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-brand-text placeholder:text-brand-muted/60"
                      placeholder="At least 10 characters"
                      data-testid="reset-pwd-input"
                    />
                  </div>
                  <PasswordStrengthMeter password={pwd} />
                </div>

                <div>
                  <label className="eyebrow block mb-2">Confirm password</label>
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl border-2 bg-brand-bg ${confirm && !matches ? "border-red-300" : "border-brand focus-within:border-brand-primary"}`}>
                    <Lock size={16} className="text-brand-muted" strokeWidth={1.6} />
                    <input
                      type="password"
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-brand-text placeholder:text-brand-muted/60"
                      placeholder="Re-type your password"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  {confirm && !matches && (
                    <p className="mt-2 text-xs text-red-600" data-testid="reset-mismatch-error">
                      Passwords don't match.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn-primary w-full justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Updating…" : (
                    <>
                      Update password
                      <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
